import { Component, effect, inject, Input, OnChanges, OnInit, signal, SimpleChanges } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { ConfirmBoxComponent } from '../../../components/confirm-box/confirm-box.component';
import { MessagingService } from '../../../components/messaging/messaging.service';
import {
  extraWideModalConfig,
  mediumModalConfig,
  smallModalConfig,
  wideModalConfig
} from '../../../consts/modal-config.const';
import {
  mediumModalConfig as mdcMediumModalConfig,
  smallModalConfig as mdcSmallModalConfig
} from '../../../consts/mdc/mdc-modal-config.const';
import { RepairerSitesSortEnum } from '../../../enums/RepairerSitesSortEnum.enum';
import { ModalConfigsHelper } from '../../../helpers/modal-configs.helper';
import { ColumnHeaders } from '../../../model/ColumnHeaders.model';
import {
  FlagUnpaidLicensePayload,
  RepairerGroup,
  RepairerSiteView,
  UserRepairerSite,
  UserRepairerSitesResponse
} from '../../../model/user.model';
import { CurrentUserService } from '../../../services/currentUser.service';
import { SysAdminService } from '../../../services/sys-admin.service';
import { ToolbarService } from '../../../services/toolbar.service';
import { EditCompanyInformationComponent } from '../../location-admin/edit-company-information/edit-company-information.component';
import { AccessRepairerSiteComponent } from '../access-repairer-site/access-repairer-site.component';
import { AddRepairerSiteComponent } from '../add-repairer-site/add-repairer-site.component';
import { ChangeCompanyNameComponent } from '../change-company-name/change-company-name.component';
import { ChangeQuotingSystemComponent } from '../change-quoting-system/change-quoting-system.component';
import { CobrowsingScreenComponent } from '../cobrowsing-screen/cobrowsing-screen.component';
import { SetDashboardDayComponent } from '../set-dashboard-day/set-dashboard-day.component';
import { RepairerSitesStore } from './repairer-sites-store.component';
import { SuspendRepairSiteComponent } from '../suspend-repair-site/suspend-repair-site.component';
import { FlagUnpaidLicenseComponent } from '../flag-unpaid-license/flag-unpaid-license.component';
import { ConfirmDialogComponent, DialogData } from '../../../components/mdc/mdc-dialog/confirm-dialog.component';
import { SetRepairerStatusComponent } from '../set-repairer-status/set-repairer-status.component';
import { RepairerSiteStatus } from '../../../types/repairerSiteStatus.type';
import {
  UNAVAILABILITY_TYPES,
  UnavailabilityTypeEnum,
  UnavailabilityTypes
} from '../../../consts/unavailability-types';
import { MatFormField, MatSuffix } from '@angular/material/form-field';
import { MatOption, MatSelect } from '@angular/material/select';
import { MatInput } from '@angular/material/input';
import { BrowserAutoFillDisabledDirective } from '../../../directives/browser-auto-fill-disabled.directive';
import { MatIcon } from '@angular/material/icon';
import { MatButton } from '@angular/material/button';
import { MatDivider } from '@angular/material/divider';
import { MatPaginator } from '@angular/material/paginator';
import { RepairerSiteTableComponent } from '../../../components/repairer-site-table/repairer-site-table.component';
import { LoadingSpinnerComponent } from '../../../components/loading-spinner/loading-spinner.component';
import {
  REPAIRER_SITES_SEARCH_PLACEHOLDER,
  REPAIRER_SITES_STATUS_OPTIONS,
  REPAIRER_SITES_TABLE_HEADERS_DEFAULT,
  REPAIRER_SITES_TABLE_HEADERS_SUSPENDED,
  REPAIRER_SITES_TABLE_HEADERS_UNPAID
} from './repairer-sites.const';

@Component({
  selector: 'app-repairer-sites',
  templateUrl: './repairer-sites.component.html',
  styleUrls: ['./repairer-sites.component.scss'],
  imports: [
    ReactiveFormsModule,
    MatFormField,
    MatSelect,
    MatOption,
    MatInput,
    BrowserAutoFillDisabledDirective,
    MatIcon,
    MatSuffix,
    MatButton,
    MatDivider,
    MatPaginator,
    RepairerSiteTableComponent,
    LoadingSpinnerComponent
  ]
})
export class RepairerSitesComponent implements OnInit, OnChanges {
  @Input() userTimezone: string;
  @Input() isToBeRefreshed = {};
  @Input() repairersGroup: RepairerGroup[];

  readonly unavailabilityTypeEnum = UnavailabilityTypeEnum;

  repairerSiteTotalResult: number;
  form: FormGroup;

  searchPlaceholder = REPAIRER_SITES_SEARCH_PLACEHOLDER;
  columnHeadersRepairerSites: ColumnHeaders[] = [...REPAIRER_SITES_TABLE_HEADERS_DEFAULT];
  statusOptions: { label: string; value: string }[] = [...REPAIRER_SITES_STATUS_OPTIONS];

  loadingRepairers = signal(false);
  pageSize = signal<number>(0);
  searchInputValue = signal<string>('');

  private toolbarService = inject(ToolbarService);
  public sysAdminService = inject(SysAdminService);
  private message = inject(MessagingService);
  private currentUser = inject(CurrentUserService);
  private matDialog = inject(MatDialog);
  private modalConfigsHelper = inject(ModalConfigsHelper);
  private repairerSitesStore = inject(RepairerSitesStore);
  private fb = inject(FormBuilder);

  constructor() {
    effect(() => {
      if (this.repairerSitesStore.refreshRepairerSites()) {
        this.form.patchValue({ searchFilter: '', statusFilter: '', page: 0 });
        this.getRepairerSites();
        this.repairerSitesStore.refreshRepairerSites.set(false);
      }
    });
  }

  ngOnInit() {
    this.form = this.fb.group({
      statusFilter: '',
      searchFilter: '',
      page: 0,
      sort: 'companyName',
      direction: ''
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes?.isToBeRefreshed?.isFirstChange() && changes?.isToBeRefreshed?.currentValue?.status) {
      this.getRepairerSites(true);
    }
  }

  getRepairerSites(resetPage: boolean = false): void {
    this.loadingRepairers.set(true);
    this.toolbarService.setCloudState('FETCHING');
    resetPage && this.page.setValue(0);
    const { searchFilter, statusFilter, page, sort, direction } = this.form.getRawValue();
    const unpaidLicense = statusFilter === 'UNPAID';
    const unavailabilityType = UNAVAILABILITY_TYPES.includes(statusFilter) ? statusFilter : '';
    const status = unpaidLicense ? '' : unavailabilityType ? 'UNAVAILABLE' : statusFilter;
    this.searchInputValue.set(searchFilter);
    this.sysAdminService
      .getRepairerSites(
        status,
        searchFilter,
        page,
        this.getSortOrder(sort || 'companyName'),
        direction === 'desc',
        unpaidLicense,
        unavailabilityType
      )
      .subscribe({
        next: (payload: UserRepairerSitesResponse) => {
          this.sysAdminService.repairerSitesList.update(() =>
            payload.repairerSites.filter((repairerSite) => !repairerSite.clientId)
          );
          this.repairerSiteTotalResult = payload.numberOfMatches;
          this.pageSize.set(payload.numberOfSitesPerPage);
          payload.numberOfMatches === 0 && this.form.patchValue({ sort: 'companyName', direction: '' });
          this.#setTableColumns(statusFilter, unpaidLicense);
          this.loadingRepairers.set(false);
          this.currentUser.timeZone = 'Australia/NSW';
          this.toolbarService.setCloudState('RESTING');
        },
        error: () => {
          this.message.error('Could not retrieve the list of repairer sites.');
          this.loadingRepairers.set(false);
          this.toolbarService.setCloudState('RESTING');
        }
      });
  }

  getSortOrder(columnName: string): RepairerSitesSortEnum {
    switch (columnName) {
      case 'companyName':
        return RepairerSitesSortEnum.COMPANY_NAME;
      case 'id':
        return RepairerSitesSortEnum.REPAIRER_ID;
      case 'mainContact.username':
        return RepairerSitesSortEnum.ADMIN_USERNAME;
      case 'mainContact.firstName':
        return RepairerSitesSortEnum.ADMINISTRATOR_NAME;
      case 'mainContact.emailAddress':
        return RepairerSitesSortEnum.ADMIN_EMAIL;
      case 'userCount':
        return RepairerSitesSortEnum.NB_USERS;
      case 'mainContact.phone':
        return RepairerSitesSortEnum.PHONE;
      case 'abn':
        return RepairerSitesSortEnum.ABN;
      case 'addressLine1':
        return RepairerSitesSortEnum.ADDRESS;
      case 'quotingSystems':
        return RepairerSitesSortEnum.QUOTING_SYSTEM;
      case 'dashboardDays':
        return RepairerSitesSortEnum.DASHBOARD_DAYS;
      case 'repairerGroup':
        return RepairerSitesSortEnum.REPAIRER_GROUP;
      case 'published':
        return RepairerSitesSortEnum.PUBLISHED_DATETIME;
      default:
        return null;
    }
  }

  getRepairerSite(id: number, reinvite: boolean = false): void {
    this.toolbarService.setCloudState('FETCHING');
    this.sysAdminService.getRepairerSite(id).subscribe({
      next: (repairerSite: RepairerSiteView) => {
        if (reinvite) {
          this.reinviteRepairer(repairerSite);
        } else {
          repairerSite.status === 'PENDING'
            ? this.editPendingRepairerSite(repairerSite)
            : this.editActiveRepairerSite(repairerSite);
        }
        this.toolbarService.setCloudState('RESTING');
      },
      error: () => {
        this.message.error('Could not retrieve the repairer site.');
        this.toolbarService.setCloudState('RESTING');
      }
    });
  }

  editActiveRepairerSite(repairer: RepairerSiteView): void {
    const dialogConfig: MatDialogConfig = wideModalConfig;
    dialogConfig.data = { repairer, repairersGroup: this.repairersGroup };
    this.matDialog
      .open(EditCompanyInformationComponent, dialogConfig)
      .afterClosed()
      .subscribe((result) => {
        if (result === 'Success') {
          this.message.confirm('Repairer Site Updated.');
          this.getRepairerSites(true);
        }
      });
  }

  kebabMenuClick(item: { action: string; param: UserRepairerSite }): void {
    switch (item?.action) {
      case 'AccessRepairerItem':
        this.confirmAccessRepairerSite(item?.param);
        break;
      case 'ChangeQuotingSystem':
        this.changeQuotingSystem(item?.param);
        break;
      case 'ChangeCompanyName':
        this.changeCompanyName(item?.param);
        break;
      case 'RevokeAllTokens':
        this.confirmRevokeAllTokens(item?.param);
        break;
      case 'SetAutoArchiveDays':
        this.setDashboardDays(item?.param);
        break;
      case 'ArchiveRepairerItem':
        this.confirmArchiveRepairerSite(item?.param);
        break;
      case 'RequestScreenSharing':
        this.requestScreenSharing(item?.param?.id);
        break;
      case 'ReInviteRepairer':
        this.getRepairerSite(item?.param?.id, true);
        break;
      case 'Delete':
        this.confirmDeletePendingInvite(item?.param?.id);
        break;
      case 'SuspendRepairSite':
        this.confirmSuspendRepairerSite(item?.param);
        break;
      case 'RestoreRepairSite':
        this.confirmRestoreRepairerSite(item?.param);
        break;
      case 'FlagUnpaidLicense':
        this.confirmFlagUnpaidLicense(item?.param);
        break;
      case 'ClearUnpaidLicense':
        this.confirmClearUnpaidLicense(item?.param);
        break;
      case 'SetRepairerStatus':
        this.confirmSetRepairerStatus(item?.param);
        break;
    }
  }

  confirmAccessRepairerSite(repairerSite: UserRepairerSite): void {
    const data = { repairerSiteId: repairerSite.id, repairerSiteName: repairerSite.companyName };
    this.matDialog.open(
      AccessRepairerSiteComponent,
      this.modalConfigsHelper.buildMediumModalConfig(data, smallModalConfig)
    );
  }

  changeCompanyName(repairerSite: UserRepairerSite): void {
    const dialogConfig = {
      ...wideModalConfig,
      width: '640px',
      data: {
        title: 'Change Company Name',
        action: 'SAVE',
        displayClose: true,
        component: ChangeCompanyNameComponent
      }
    };
    this.repairerSitesStore.getRepairerSite({ repairerSite, dialogConfig });
  }

  changeQuotingSystem(repairerSite: UserRepairerSite): void {
    const dialogConfig: MatDialogConfig = wideModalConfig;
    dialogConfig.data = { repairerSiteId: repairerSite.id };
    this.matDialog
      .open(ChangeQuotingSystemComponent, dialogConfig)
      .afterClosed()
      .subscribe((result) => {
        if (result === 'Success') {
          this.message.confirm('Quoting System Changed.');
          this.getRepairerSites(true);
        }
      });
  }

  confirmRevokeAllTokens(event: any): void {
    const data = {
      title: 'Revoke All Tokens',
      alertMessage: 'All users tokens will be revoked. They will need to redo the linking process.',
      confirmButton: 'REVOKE'
    };
    this.matDialog
      .open(ConfirmBoxComponent, this.modalConfigsHelper.buildMediumModalConfig(data, smallModalConfig))
      .afterClosed()
      .subscribe((result) => {
        if (result === 'REVOKE') {
          event.isQuotingSystem
            ? this.revokingTokensOfQuotingSystem(event.id)
            : this.revokingTokensForRepairSite(event.id);
        }
      });
  }

  revokingTokensOfQuotingSystem(repairerSiteId: number): void {
    this.toolbarService.setCloudState('FETCHING');
    this.sysAdminService.revokeTokensOfQuotingSystem(repairerSiteId).subscribe({
      next: () => this.message.confirm('Tokens revoked for Quoting System.'),
      error: () => {
        this.message.error('Could not revoke tokens for Quoting System.');
        this.toolbarService.setCloudState('RESTING');
      },
      complete: () => this.toolbarService.setCloudState('RESTING')
    });
  }

  revokingTokensForRepairSite(repairerSiteId: number): void {
    this.toolbarService.setCloudState('FETCHING');
    this.sysAdminService.revokeTokensOfRepairerSite(repairerSiteId).subscribe({
      next: () => this.message.confirm('Tokens revoked for Repairer Site.'),
      error: () => {
        this.message.error('Could not revoke tokens for Repairer Site.');
        this.toolbarService.setCloudState('RESTING');
      },
      complete: () => this.toolbarService.setCloudState('RESTING')
    });
  }

  setDashboardDays(repairerSite: UserRepairerSite): void {
    const dialogConfig = {
      ...wideModalConfig,
      panelClass: 'wide-modal',
      data: {
        title: 'Set Autoarchive Days',
        action: 'SET AUTOARCHIVE DAYS',
        displayClose: true,
        component: SetDashboardDayComponent
      }
    };
    this.repairerSitesStore.getRepairerSite({ repairerSite, dialogConfig });
  }

  confirmArchiveRepairerSite(repairerSite: UserRepairerSite): void {
    const data = {
      title: 'Archive Repairer Site',
      alertMessage: 'All users for this site will be disabled and the repairer site will no longer be accessible.',
      confirmButton: 'ARCHIVE'
    };
    this.matDialog
      .open(ConfirmBoxComponent, this.modalConfigsHelper.buildMediumModalConfig(data, smallModalConfig))
      .afterClosed()
      .subscribe((result) => {
        if (result === 'ARCHIVE') {
          this.archiveRepairerSite(repairerSite.id);
        }
      });
  }

  archiveRepairerSite(repairerSiteId: number): void {
    this.toolbarService.setCloudState('SAVING');
    this.sysAdminService.archiveRepairerSite(repairerSiteId).subscribe({
      next: () => this.toolbarService.setCloudState('RESTING'),
      error: () => {
        this.message.error('Repairer Site could not be archived.');
        this.toolbarService.setCloudState('RESTING');
      },
      complete: () => {
        this.message.confirm('Repairer Site Archived');
        this.getRepairerSites(true);
      }
    });
  }

  confirmSuspendRepairerSite(repairerSite: UserRepairerSite): void {
    this.matDialog
      .open(SuspendRepairSiteComponent, { ...mediumModalConfig, width: '640px', data: repairerSite })
      .afterClosed()
      .subscribe((result: { reason: string }) => {
        if (result) {
          this.suspendRepairerSite(repairerSite, result.reason);
        }
      });
  }

  suspendRepairerSite(repairerSite: UserRepairerSite, reason: string): void {
    this.toolbarService.setCloudState('SAVING');
    this.sysAdminService.suspendRepairerSite(repairerSite.id, reason).subscribe({
      next: () => this.toolbarService.setCloudState('RESTING'),
      error: () => {
        this.message.error(`Could not suspend ${repairerSite.companyName}.`);
        this.toolbarService.setCloudState('RESTING');
      },
      complete: () => {
        this.message.confirm(`${repairerSite.companyName} suspended.`);
        this.getRepairerSites(true);
      }
    });
  }

  confirmSetRepairerStatus(repairerSite: UserRepairerSite): void {
    this.matDialog
      .open(SetRepairerStatusComponent, { ...mdcMediumModalConfig, width: '640px', data: repairerSite.companyName })
      .afterClosed()
      .subscribe((result: { status: RepairerSiteStatus; unavailabilityType?: UnavailabilityTypes }) => {
        if (result) {
          const { status, unavailabilityType } = result;
          this.setRepairerStatus(repairerSite, status, unavailabilityType);
        }
      });
  }

  setRepairerStatus(
    repairerSite: UserRepairerSite,
    status: RepairerSiteStatus,
    unavailabilityType?: UnavailabilityTypes
  ): void {
    const visibleStatus = unavailabilityType ? this.unavailabilityTypeEnum[unavailabilityType] : 'Active';
    this.toolbarService.setCloudState('SAVING');
    this.sysAdminService.setRepairerStatus(repairerSite.id, status, unavailabilityType).subscribe({
      next: () => this.toolbarService.setCloudState('RESTING'),
      error: () => {
        this.message.error(`Could not set ${repairerSite.companyName} status to ${visibleStatus}.`);
        this.toolbarService.setCloudState('RESTING');
      },
      complete: () => {
        this.message.confirm(`${repairerSite.companyName} status set to ${visibleStatus}.`);
        this.getRepairerSites(true);
      }
    });
  }

  confirmRestoreRepairerSite(repairerSite: UserRepairerSite): void {
    const data = {
      title: 'Restore Repair Site',
      alertMessage: `${repairerSite.companyName} will be restored and will be able to receive and action jobs.`,
      confirmButton: 'RESTORE',
      hideCannotBeUndone: true
    };
    this.matDialog
      .open(ConfirmBoxComponent, this.modalConfigsHelper.buildMediumModalConfig(data, smallModalConfig))
      .afterClosed()
      .subscribe({
        next: (result) => {
          if (result === 'RESTORE') {
            this.restoreRepairerSite(repairerSite);
          }
        }
      });
  }

  confirmFlagUnpaidLicense(repairerSite: UserRepairerSite): void {
    this.matDialog
      .open(FlagUnpaidLicenseComponent, { ...mdcMediumModalConfig, width: '640px', data: repairerSite })
      .afterClosed()
      .subscribe((payload: FlagUnpaidLicensePayload) => {
        if (payload) {
          this.#flagUnpaidLicense(repairerSite.id, payload);
        }
      });
  }

  #flagUnpaidLicense(repairerSiteId: number, payload: FlagUnpaidLicensePayload): void {
    this.toolbarService.setCloudState('SAVING');
    this.sysAdminService.flagUnpaidLicense(repairerSiteId, payload).subscribe({
      next: () => this.toolbarService.setCloudState('RESTING'),
      error: () => {
        this.message.error(`Could not flag the unpaid license.`);
        this.toolbarService.setCloudState('RESTING');
      },
      complete: () => {
        this.message.confirm(`The unpaid license has been flagged.`);
        this.getRepairerSites(true);
      }
    });
  }

  confirmClearUnpaidLicense(repairerSite: UserRepairerSite): void {
    const data: DialogData = {
      title: 'Clear Unpaid License',
      message: `The repairer will no longer see the warning that they have an overdue license fee.`,
      confirmButton: 'CLEAR'
    };

    this.matDialog
      .open(ConfirmDialogComponent, { ...mdcSmallModalConfig, data, panelClass: 'medium-small-modal' })
      .afterClosed()
      .subscribe((result) => {
        if (result === 'CLEAR') {
          this.#clearUnpaidLicense(repairerSite.id);
        }
      });
  }

  #clearUnpaidLicense(repairerSiteId: number): void {
    this.toolbarService.setCloudState('SAVING');
    this.sysAdminService.clearUnpaidLicense(repairerSiteId).subscribe({
      next: () => this.toolbarService.setCloudState('RESTING'),
      error: () => {
        this.message.error(`Could not clear the unpaid license.`);
        this.toolbarService.setCloudState('RESTING');
      },
      complete: () => {
        this.message.confirm(`The unpaid license has been cleared.`);
        this.getRepairerSites(true);
      }
    });
  }

  restoreRepairerSite(repairerSite: UserRepairerSite): void {
    this.toolbarService.setCloudState('SAVING');
    this.sysAdminService.restoreRepairerSite(repairerSite.id).subscribe({
      next: () => this.toolbarService.setCloudState('RESTING'),
      error: () => {
        this.message.error(`Could not restore ${repairerSite.companyName}.`);
        this.toolbarService.setCloudState('RESTING');
      },
      complete: () => {
        this.message.confirm(`${repairerSite.companyName} restored.`);
        this.getRepairerSites(true);
      }
    });
  }

  requestScreenSharing(id: number): void {
    const dialogConfig: MatDialogConfig = extraWideModalConfig;
    dialogConfig.panelClass = 'full-screen-modal';
    dialogConfig.data = {
      repairerSiteId: id,
      sysadminName: `${this.currentUser.currentUserValue?.firstName} ${this.currentUser?.currentUserValue?.lastName}`
    };
    this.matDialog.open(CobrowsingScreenComponent, dialogConfig);
  }

  reinviteRepairer(repairerSite: RepairerSiteView) {
    const dialogConfig: MatDialogConfig = wideModalConfig;
    dialogConfig.data = {
      repairerSite,
      repairersGroup: this.repairersGroup
    };
    this.matDialog
      .open(AddRepairerSiteComponent, dialogConfig)
      .afterClosed()
      .subscribe((result) => {
        if (result === 'Success') {
          this.message.confirm('Repairer is re-invited');
          this.getRepairerSites(true);
        }
      });
  }

  confirmDeletePendingInvite(repairerSiteId: number) {
    const data = {
      title: 'Delete Pending Invite',
      alertMessage: 'Pending Invite will be deleted.',
      confirmButton: 'DELETE'
    };
    this.matDialog
      .open(ConfirmBoxComponent, this.modalConfigsHelper.buildMediumModalConfig(data, smallModalConfig))
      .afterClosed()
      .subscribe((result) => {
        if (result === 'DELETE') {
          this.deletePendingInvite(repairerSiteId);
        }
      });
  }

  deletePendingInvite(repairerSiteId: number): void {
    this.toolbarService.setCloudState('SAVING');
    this.sysAdminService.deleteRepairerSite(repairerSiteId).subscribe({
      next: () => this.toolbarService.setCloudState('RESTING'),
      error: () => {
        this.message.error('Repairer Site could not be deleted.');
        this.toolbarService.setCloudState('RESTING');
      },
      complete: () => {
        this.message.confirm('Repairer Site Deleted');
        this.getRepairerSites(true);
      }
    });
  }

  editPendingRepairerSite(repairerSite: RepairerSiteView): void {
    const dialogConfig: MatDialogConfig = wideModalConfig;
    dialogConfig.data = {
      repairerSite,
      title: 'Edit Repairer',
      isEditRepairerSite: true,
      repairersGroup: this.repairersGroup
    };
    this.matDialog
      .open(AddRepairerSiteComponent, dialogConfig)
      .afterClosed()
      .subscribe((result) => {
        if (result === 'Success') {
          this.message.confirm('Repairer Site Edited');
          this.getRepairerSites(true);
        }
      });
  }

  pageSearch(page: number): void {
    this.form.patchValue({ page });
    this.getRepairerSites();
  }

  sortedSearch(sort?: string, direction?: string): void {
    this.form.patchValue({ sort: sort || 'companyName', direction });
    this.getRepairerSites();
  }

  #setTableColumns(status: RepairerSiteStatus, unpaidLicense: boolean): void {
    if (unpaidLicense) {
      this.columnHeadersRepairerSites = [...REPAIRER_SITES_TABLE_HEADERS_UNPAID];
    } else if (status === 'SUSPENDED') {
      this.columnHeadersRepairerSites = [...REPAIRER_SITES_TABLE_HEADERS_SUSPENDED];
    } else {
      this.columnHeadersRepairerSites = [...REPAIRER_SITES_TABLE_HEADERS_DEFAULT];
    }
  }

  get searchFilter(): FormControl<string> {
    return this.form.controls.searchFilter as FormControl<string>;
  }
  get page(): FormControl<number> {
    return this.form.controls.page as FormControl<number>;
  }
}
